import { APP_INITIALIZER, NgModule } from '@angular/core';
import { canActivate, redirectLoggedInTo } from '@angular/fire/auth-guard';
import { RouterModule } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { AuthMethodsService, CustomAuthComponents, ForgotPasswordModule, LoginModule, RegisterModule } from 'ngx-zcs-auth';
import { HeaderOptionsModule } from 'ngx-zcs-blocks';
import { SimpleButtonComponent, SimpleInputComponent, TooltipModule } from 'ngx-zcs-components';
import { MatIconModule } from '@angular/material/icon';

import { ForgotPasswordService } from '../service/forgot-password.service';
import { LoginService } from '../service/login.service';
import { RegisterService } from '../service/register.service';
import { LanguageSelectorComponent } from '../../shared/standalone';
import { SharedModule } from '../../shared/shared.module';
import { CustomLoginComponent } from './custom-login/custom-login.component';
import { CustomRegisterComponent } from './custom-register/custom-register.component';
import { CustomForgotPasswordComponent } from './custom-forgot-password/custom-forgot-password.component';
import { AzzurroInverterComponent } from './azzurro-inverter/azzurro-inverter.component';

const redirectLoggedInToHome = () => redirectLoggedInTo(['customer', 'plants']);

const initializeAuthService = (
  authService: AuthMethodsService,
  loginService: LoginService,
  forgotPasswordService: ForgotPasswordService,
  registerService: RegisterService,
  translateService: TranslateService,
) => () => {
  const initFn = async () => {
    await translateService.get('i18n.common.emailRequired').toPromise();
    authService.init({
      login: loginService,
      forgotPassword: forgotPasswordService,
      register: registerService,
    }, {
      templateImgUrl: 'assets/images/svg/azzurro.svg',
    }, {
      forgotPassword: {
        emailRequired: translateService.instant('i18n.common.emailRequired'),
        emailInvalid: translateService.instant('i18n.common.emailInvalid'),
        login: translateService.instant('i18n.common.login'),
        sendPswReset: translateService.instant('i18n.auth.forgotPassword.sendPswReset'),
      },
      login: {
        emailRequired: translateService.instant('i18n.common.emailRequired'),
        emailInvalid: translateService.instant('i18n.common.emailInvalid'),
        passwordRequired: translateService.instant('i18n.common.passwordRequired'),
        login: translateService.instant('i18n.common.login'),
        signUp: translateService.instant('i18n.common.signUp'),
        forgotPassword: translateService.instant('i18n.common.forgotPassword'),
      },
      register: {
        usernameRequired: translateService.instant('i18n.auth.register.usernameRequired'),
        emailInvalid: translateService.instant('i18n.common.emailInvalid'),
        emailRequired: translateService.instant('i18n.common.emailRequired'),
        passwordRequired: translateService.instant('i18n.common.passwordRequired'),
        confirmPasswordRequired: translateService.instant('i18n.auth.register.confirmPasswordRequired'),
        passwordsDoNotMatch: translateService.instant('i18n.auth.register.passwordsDoNotMatch'),
        login: translateService.instant('i18n.common.login'),
        signUp: translateService.instant('i18n.common.signUp'),
      },
    });
  };

  initFn();

  translateService.onTranslationChange.subscribe(() => initFn());
  translateService.onLangChange.subscribe(() => initFn());
};

@NgModule({
  declarations: [
    CustomForgotPasswordComponent,
    CustomLoginComponent,
    CustomRegisterComponent,
    AzzurroInverterComponent,
  ],
  imports: [
    CommonModule,
    ForgotPasswordModule,
    HeaderOptionsModule,
    LanguageSelectorComponent,
    LoginModule,
    MatIconModule,
    RegisterModule,
    RouterModule,
    SharedModule,
    SimpleButtonComponent,
    SimpleInputComponent,
    TooltipModule,
  ],
  providers: [
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAuthService,
      multi: true,
      deps: [
        AuthMethodsService,
        LoginService,
        ForgotPasswordService,
        RegisterService,
        TranslateService,
      ],
    },
    CustomAuthComponents({
      forgotPage: CustomForgotPasswordComponent,
      guard: canActivate(redirectLoggedInToHome),
      loginPage: CustomLoginComponent,
      registerPage: CustomRegisterComponent,
    }),
  ],
  exports: [
    CustomLoginComponent,
  ],
})
export class CustomAuthModule {}
